import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';

import ButtonMain from '@components/buttons/ButtonMain';
import InputMask from '@components/inputs/InputMask';
import InputSelect from '@components/inputs/InputSelect';
import InputText from '@components/inputs/InputText';
import Paragraph from '@components/texts/Paragraph';
import SubtitleSecondary from '@components/texts/SubtitleSecondary';
import { ConfigLabel, ConfigValues } from '@config/index';
import Indication from '@models/Indication';
import Payment from '@models/Payment';
import { apiRebox } from '@services/index';
import { formatDate, formatMoney } from '@utils/formatters';

import {
  Container,
  DividingLine,
  FormIndication,
  Sections,
  SectionsGroup,
  SectionsItem,
} from './styles';

interface IProps {
  indication: Indication;
}

const FormIndicationView: React.FC<IProps> = ({ indication }) => {
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [disabledForEditing, _setDisabledForEditing] = useState<boolean>(
    indication?.status === ConfigValues.rebox.indication.status.created ||
      indication?.status === ConfigValues.rebox.indication.status.effective,
  );

  const [payments, setPayments] = useState<Payment[]>([]);

  const getPaymentsForIndication = useCallback(async () => {
    try {
      setLoading(true);
      const {
        data: { data: paymentsData },
      } = await apiRebox.get('/payments', {
        params: {
          what_is_being_paid: 'indications',
          paying_item_with_id: indication.id,
          reference_type: 'CONTRACTS',
        },
      });
      console.log(paymentsData);
      setPayments(paymentsData);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getPaymentsForIndication();
  }, [indication]);

  return (
    <Container>
      <FormIndication ref={formRef} onSubmit={() => []} initialData={{}}>
        {payments.map((payment: Payment) => (
          <>
            <Sections key={payment.id}>
              <SubtitleSecondary
                textAlign="start"
                nameColor="blue"
                fontSize={14}
              >
                {payment.installment}ª parcela
              </SubtitleSecondary>
              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Valor para ser recebido
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Valor que o afiliado irá receber
                  </Paragraph>

                  <InputText
                    name="amount"
                    placeholder="Apenas números"
                    tabIndex={3}
                    disabled={disabledForEditing}
                    isDisable={disabledForEditing}
                    value={formatMoney.fromNumberToPrice(payment.amount)}
                    style={{
                      cursor: disabledForEditing ? 'not-allowed' : 'auto',
                    }}
                  />
                </SectionsItem>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Forma de pagamento
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Forma de pagamento escolhida pelo usuário indicado
                  </Paragraph>

                  <InputSelect
                    name="form_of_payment"
                    options={
                      ConfigLabel.rebox.others.indication.form_of_payment
                    }
                    tabIndex={3}
                    selectedDefault={
                      payment.form_of_payment === 'UNDEFINED'
                        ? 'BOLETO'
                        : payment.form_of_payment
                    }
                    disabled={disabledForEditing}
                    isDisable={disabledForEditing}
                    style={{
                      cursor: disabledForEditing ? 'not-allowed' : 'auto',
                    }}
                  />
                </SectionsItem>
              </SectionsGroup>
              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Situação da cobrança
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Define o status que a cobrança está atualmente
                  </Paragraph>

                  <InputSelect
                    name="status"
                    options={ConfigLabel.rebox.others.payment.status}
                    selectedDefault={payment.status}
                    tabIndex={3}
                    disabled={disabledForEditing}
                    isDisable={disabledForEditing}
                    style={{
                      cursor: disabledForEditing ? 'not-allowed' : 'auto',
                    }}
                  />
                </SectionsItem>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Data de criação
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Data em que a cobrança foi gerada
                  </Paragraph>

                  <InputMask
                    name="date_created"
                    mask="99/99/9999"
                    placeholder="Informe a data"
                    value={formatDate.addMask(payment.date_created)}
                    tabIndex={1}
                    disabled={disabledForEditing}
                    isDisable={disabledForEditing}
                    style={{
                      cursor: disabledForEditing ? 'not-allowed' : 'auto',
                    }}
                  />
                </SectionsItem>
              </SectionsGroup>
              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Descrição
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Informação adicional sobre a cobrança
                  </Paragraph>

                  <InputText
                    name="description"
                    tabIndex={3}
                    value={payment.description}
                    disabled={disabledForEditing}
                    isDisable={disabledForEditing}
                    style={{
                      cursor: disabledForEditing ? 'not-allowed' : 'auto',
                    }}
                  />
                </SectionsItem>
              </SectionsGroup>
            </Sections>
            {
              /* Somente para aparecer abaixo da primeira parcela */
              payment.installment === 1 && <DividingLine />
            }
          </>
        ))}
      </FormIndication>
    </Container>
  );
};

export default FormIndicationView;
